import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Fade } from '@mui/material';
import { Alert } from '@material-ui/lab';
import RestoreIcon from '@material-ui/icons/Restore';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import InfoIcon from '@material-ui/icons/Info';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import { shortenedDate } from 'Consts/DateFormats';
import useMobileLayout from 'Hooks/useMobileLayout';

const styles = (theme) => ({
  root: {},
  alertMobile: {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiAlert-action': {
      margin: theme.spacing(1, 0, 1, 0),
      padding: 0,
    },
  },
  clearScheduleButton: {
    marginRight: theme.spacing(1),
  },
});

const OutOfOfficeAlertMessage = ({
  className,
  classes,
  scheduledDates,
  setScheduledDates,
  onClearSchedule,
  outOfOfficeEnabled,
}) => {
  // Format dates for Alert message
  const formattedDate = date => moment(date).format(shortenedDate);

  // Alert title / message
  const alertTitle = !outOfOfficeEnabled ? 'No out of office time set.' : 'Out of office time is enabled!';
  const enabledOutOfOfficeMessage = `Your away message will send from ${formattedDate(scheduledDates.start)} to ${formattedDate(scheduledDates.end)}.`;
  const disabledOutOfOfficeMessage = 'Please select your away dates, and write a custom message below.';

  const isMobile = useMobileLayout();

  return (
    <div
      className={classNames(classes.root, className)}
      test={`${outOfOfficeEnabled ? 'enabled' : 'disabled'}-out-of-office-alert-message`}
    >
      <Fade in>
        <Alert
          className={classNames({
            [classes.alertMobile]: isMobile,
          })}
          color={outOfOfficeEnabled ? 'success' : 'warning'}
          icon={outOfOfficeEnabled ? <AccessTimeIcon /> : <InfoIcon />}
          action={outOfOfficeEnabled && (
            <Button
              className={classNames({
                [classes.clearScheduleButton]: !isMobile,
              })}
              color={'red'}
              variant={'contained'}
              disableElevation
              startIcon={<RestoreIcon />}
              onClick={async () => {
                setScheduledDates({
                  start: null,
                  end: null,
                });
                await onClearSchedule();
              }}
              fullWidth={isMobile}
              test={'clear-out-of-office-button'}
            >
              {'Clear Schedule'}
            </Button>)
          }
        >
          <Typography
            variant={'h6'}
            color={outOfOfficeEnabled ? 'secondary' : 'primary'}
            weight={'bold'}
          >
            {alertTitle}
          </Typography>
          <Typography
            variant={'h6'}
            color={outOfOfficeEnabled ? 'secondary' : 'primary'}
            weight={'book'}
          >
            {outOfOfficeEnabled ? enabledOutOfOfficeMessage : disabledOutOfOfficeMessage}
          </Typography>
        </Alert>
      </Fade>
    </div>
  );
};

OutOfOfficeAlertMessage.defaultProps = {
  className: '',
};

OutOfOfficeAlertMessage.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  scheduledDates: PropTypes.object.isRequired,
  setScheduledDates: PropTypes.func.isRequired,
  onClearSchedule: PropTypes.func.isRequired,
  outOfOfficeEnabled: PropTypes.bool.isRequired,
};

export default withStyles(styles)(OutOfOfficeAlertMessage);
